import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import "@/styles/language-welcome.css";

const CHOICE_KEY = "lightpay:language-chosen:v1";
const LANGUAGES = [
  { code: "ru", name: "Русский", glyph: "А", description: "Russian" },
  { code: "en", name: "English", glyph: "A", description: "Английский" },
  { code: "es", name: "Español", glyph: "Ñ", description: "Spanish" },
  { code: "zh", name: "中文", glyph: "文", description: "Chinese" },
];

export function hasChosenLanguage() {
  try {
    return window.localStorage.getItem(CHOICE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function welcomeTheme() {
  try {
    const saved = window.localStorage.getItem("theme");
    if (saved === "dark" || saved === "light") return saved;
  } catch (_) {}
  return window.Telegram?.WebApp?.colorScheme || "dark";
}

export default function InitialLanguageGate({ children }) {
  const { i18n } = useTranslation();
  const [chosen, setChosen] = useState(hasChosenLanguage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [theme, setTheme] = useState(welcomeTheme);

  useEffect(() => {
    if (chosen) return;
    // The app's authentication and splash mount only after this first step.
    const tg = window.Telegram?.WebApp;
    tg?.ready?.();
    tg?.expand?.();
    const syncTheme = () => {
      const next = welcomeTheme();
      setTheme(next);
      const color = next === "dark" ? "#0b1020" : "#f6f8fc";
      try {
        tg?.setHeaderColor?.(color);
        tg?.setBackgroundColor?.(color);
        tg?.setBottomBarColor?.(color);
      } catch (_) {}
    };
    syncTheme();
    tg?.onEvent?.("themeChanged", syncTheme);
    return () => tg?.offEvent?.("themeChanged", syncTheme);
  }, [chosen]);

  const choose = async (code) => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      await i18n.changeLanguage(code);
      document.documentElement.lang = code;
      try {
        window.localStorage.setItem(CHOICE_KEY, "1");
      } catch (_) {
        // Restricted webviews can still continue with this session's choice.
      }
      setChosen(true);
    } catch (_) {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (chosen) return children;

  return (
    <main
      data-testid="initial-language-gate"
      data-theme={theme}
      className="lp-language"
      aria-labelledby="initial-language-title"
      aria-busy={busy}
    >
      <div className="lp-language__content">
        <div className="lp-language__brand" aria-label="LightPay">
          <span className="lp-language__mark" aria-hidden="true">
            <svg viewBox="60 10 80 180"><path d="M112,20 L76,106 L100,106 L86,180 L126,92 L102,92 Z" fill="currentColor" /></svg>
          </span>
          <span>Light<span className="lp-language__brand-accent">Pay</span></span>
        </div>
        <header className="lp-language__heading">
          <h1 id="initial-language-title" lang="ru">Выберите язык</h1>
          <p lang="en">Choose your language</p>
        </header>
        <div className="lp-language__options">
          {LANGUAGES.map(({ code, name, glyph, description }) => (
            <button
              key={code}
              type="button"
              data-testid={`initial-language-${code}`}
              disabled={busy}
              onClick={() => choose(code)}
              className="lp-language__option"
            >
              <span className="lp-language__option-top" aria-hidden="true">
                <span className="lp-language__glyph">{glyph}</span>
                <ArrowUpRight size={18} strokeWidth={1.7} />
              </span>
              <span lang={code} className="lp-language__name">{name}</span>
              <span className="lp-language__description">{description}</span>
            </button>
          ))}
        </div>
        <footer className="lp-language__footer">
          <span lang="ru">Язык можно изменить в профиле</span>
          <span lang="en">You can change it later in your profile</span>
        </footer>
        {error && <p role="alert" className="lp-language__error">Не удалось выбрать язык. Попробуйте ещё раз.<br />Could not select a language. Please try again.</p>}
      </div>
    </main>
  );
}
